import React, { useState, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTranslations } from '../../hooks/useTranslations';

const ValueOverTimeChart = ({ data = [] }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('all');

  const t = useTranslations()
    
  const timePeriods = [
    { key: '3m', label: '3M', months: 3 },
    { key: '6m', label: '6M', months: 6 },
    { key: '1y', label: '1Y', months: 12 },
    { key: '2y', label: '2Y', months: 24 },
    { key: '5y', label: '5Y', months: 60 },
    { key: 'all', label: t.charts.periods.all, months: null }
  ];

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    if (selectedPeriod === 'all') return data;
    const period = timePeriods.find(p => p.key === selectedPeriod);
    if (!period || !period.months) return data;
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - period.months);
    return data.filter(item => new Date(item.date) >= cutoffDate);
  }, [data, selectedPeriod]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const date = new Date(label);
      const formattedDate = date.toLocaleDateString('he-IL', { month: 'long', year: 'numeric' });
      return (
        <div className="bg-white p-2 sm:p-3 border border-gray-200 rounded-lg shadow-lg text-xs sm:text-sm">
          <p className="text-gray-600 font-medium">{formattedDate}</p>
          <p className="text-blue-600 font-semibold">{payload[0].value} {t.charts.appointments}</p>
        </div>
      );
    }
    return null;
  };

  const formatXAxis = (tickItem) => {
    const date = new Date(tickItem);
    const isMobile = window.innerWidth < 640;
    return date.toLocaleDateString('he-IL', { 
      month: isMobile ? 'numeric' : 'short', 
      year: '2-digit' 
    });
  };

  const sampleData = data.length === 0 ? [
    { date: '2023-01-01', value: 45 }, { date: '2023-02-01', value: 52 }, { date: '2023-03-01', value: 48 },
    { date: '2023-04-01', value: 61 }, { date: '2023-05-01', value: 55 }, { date: '2023-06-01', value: 67 },
    { date: '2023-07-01', value: 71 }, { date: '2023-08-01', value: 58 }, { date: '2023-09-01', value: 76 },
    { date: '2023-10-01', value: 69 }, { date: '2023-11-01', value: 83 }, { date: '2023-12-01', value: 78 },
    { date: '2024-01-01', value: 82 }, { date: '2024-02-01', value: 89 }, { date: '2024-03-01', value: 95 },
    { date: '2024-04-01', value: 101 }, { date: '2024-05-01', value: 97 }, { date: '2024-06-01', value: 108 },
    { date: '2024-07-01', value: 112 }, { date: '2024-08-01', value: 105 }
  ] : data;

  const chartData = data.length === 0 ? sampleData : filteredData;

  // Calculate responsive intervals and settings
  const getResponsiveSettings = () => {
    const isMobile = window.innerWidth < 640;
    const isTablet = window.innerWidth < 1024;
    
    let interval = 0;
    if (chartData.length > 12 && isMobile) interval = Math.floor(chartData.length / 6);
    else if (chartData.length > 8 && isTablet) interval = Math.floor(chartData.length / 8);
    
    return {
      fontSize: isMobile ? 8 : 10,
      angle: isMobile ? -45 : -35,
      height: isMobile ? 50 : 60,
      interval: interval || 'preserveStartEnd',
      margin: isMobile ? { top: 5, right: 2, left: 2, bottom: 5 } : { top: 10, right: 15, left: 0, bottom: 0 }
    };
  };

  const responsiveSettings = getResponsiveSettings();

  return (
    <div className="w-full bg-white rounded-xl shadow-sm border border-gray-100 p-2 sm:p-4 lg:p-6">
      <div className="mb-3 sm:mb-4 lg:mb-6">
        <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800 mb-1">
          {t.charts.valueOverTime.title}
        </h3>
        <p className="text-gray-600 text-xs sm:text-sm">
          {t.charts.valueOverTime.description}
        </p>
      </div>

      <div className="flex flex-wrap gap-1 sm:gap-2 justify-center mb-3 sm:mb-4 lg:mb-6">
        {timePeriods.map((period) => (
          <button
            key={period.key}
            onClick={() => setSelectedPeriod(period.key)}
            className={`px-2 py-1 sm:px-3 sm:py-1.5 lg:px-4 lg:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
              selectedPeriod === period.key 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {period.label}
          </button>
        ))}
      </div>

      <div className="w-full overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        <div className="min-w-[280px] sm:min-w-[400px] lg:min-w-full h-48 sm:h-64 lg:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={responsiveSettings.margin}>
              <defs>
                <linearGradient id="colorAppointments" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" opacity={0.6} />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatXAxis}
                stroke="#6B7280"
                fontSize={responsiveSettings.fontSize}
                axisLine={false}
                tickLine={false}
                interval={responsiveSettings.interval}
                angle={responsiveSettings.angle}
                textAnchor="end"
                height={responsiveSettings.height}
                tick={{ fontSize: responsiveSettings.fontSize }}
              />
              <YAxis 
                stroke="#6B7280"
                fontSize={responsiveSettings.fontSize}
                axisLine={false}
                tickLine={false}
                width={25}
                tick={{ fontSize: responsiveSettings.fontSize }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#3B82F6"
                strokeWidth={2}
                fill="url(#colorAppointments)"
                dot={{ fill: '#3B82F6', strokeWidth: 1, r: 2 }}
                activeDot={{ r: 4, stroke: '#3B82F6', strokeWidth: 2, fill: '#ffffff' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-3 sm:mt-4 lg:mt-6 grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
          <div className="text-sm sm:text-xl lg:text-2xl font-bold text-gray-800">
            {chartData.length > 0 ? Math.max(...chartData.map(d => d.value)) : 0}
          </div>
          <div className="text-xs sm:text-sm text-gray-600">{t.charts.valueOverTime.peakMonth}</div>
        </div>
        <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
          <div className="text-sm sm:text-xl lg:text-2xl font-bold text-gray-800">
            {chartData.length > 0 ? Math.round(chartData.reduce((sum, d) => sum + d.value, 0) / chartData.length) : 0}
          </div>
          <div className="text-xs sm:text-sm text-gray-600">{t.charts.valueOverTime.average}</div>
        </div>
        <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
          <div className="text-sm sm:text-xl lg:text-2xl font-bold text-gray-800">
            {chartData.length > 0 ? chartData.reduce((sum, d) => sum + d.value, 0) : 0}
          </div>
          <div className="text-xs sm:text-sm text-gray-600">{t.charts.valueOverTime.total}</div>
        </div>
        <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
          <div className="text-sm sm:text-xl lg:text-2xl font-bold text-blue-600">
            {chartData.length > 1 && chartData[chartData.length - 1].value > chartData[chartData.length - 2].value ? '↗' : 
             chartData.length > 1 && chartData[chartData.length - 1].value < chartData[chartData.length - 2].value ? '↘' : '→'}
          </div>
          <div className="text-xs sm:text-sm text-gray-600">{t.charts.valueOverTime.trend}</div>
        </div>
      </div>
    </div>
  );
};

export default ValueOverTimeChart;